import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useRestaurant } from '../../context/RestaurantContext';
import { 
  Plus, Edit, Trash2, Users, MapPin, 
  Settings, CheckCircle, XCircle, Save, X, Store, LayoutGrid, Move, ZoomIn, ZoomOut,
  QrCode, Download, Printer, Bluetooth, AlertCircle
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import printBridge from '../../utils/printBridge';

const ZONES = ['Main', 'Patio', 'Private Room', 'Bar', 'Terrace', 'Garden', 'VIP'];

const ZONE_COLORS = {
  'Main': { bg: 'bg-blue-100', border: 'border-blue-400', text: 'text-blue-700', fill: '#DBEAFE' },
  'Patio': { bg: 'bg-green-100', border: 'border-green-400', text: 'text-green-700', fill: '#DCFCE7' },
  'Private Room': { bg: 'bg-purple-100', border: 'border-purple-400', text: 'text-purple-700', fill: '#F3E8FF' },
  'Bar': { bg: 'bg-amber-100', border: 'border-amber-400', text: 'text-amber-700', fill: '#FEF3C7' },
  'Terrace': { bg: 'bg-teal-100', border: 'border-teal-400', text: 'text-teal-700', fill: '#CCFBF1' },
  'Garden': { bg: 'bg-lime-100', border: 'border-lime-400', text: 'text-lime-700', fill: '#ECFCCB' },
  'VIP': { bg: 'bg-rose-100', border: 'border-rose-400', text: 'text-rose-700', fill: '#FFE4E6' },
};

export default function TableManagement() {
  const { t } = useTranslation();
  const { api } = useAuth();
  // Use shared restaurant context - layout handles "no restaurant" case
  const { restaurants, selectedRestaurant, setSelectedRestaurant } = useRestaurant();
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showTableDialog, setShowTableDialog] = useState(false);
  const [editingTable, setEditingTable] = useState(null);
  
  // Floor plan state
  const [floorPlanZoom, setFloorPlanZoom] = useState(1);
  const [draggingTable, setDraggingTable] = useState(null);
  const [tablePositions, setTablePositions] = useState({});
  const floorPlanRef = useRef(null);
  
  // Printer state
  const [printerConnected, setPrinterConnected] = useState(false);
  const [printing, setPrinting] = useState(false);
  
  // Form states
  const [tableForm, setTableForm] = useState({
    table_number: '',
    capacity: 2,
    zone: 'Main',
    description: '',
    is_active: true
  });

  const fetchTables = useCallback(async () => {
    if (!selectedRestaurant?.id) return;
    try {
      const response = await api.get(`/restaurants/${selectedRestaurant.id}/tables?include_inactive=true`);
      setTables(response.data);
      // Initialize table positions from stored data or generate default positions
      const positions = {};
      response.data.forEach((table, idx) => {
        positions[table.id] = table.position || {
          x: 50 + (idx % 5) * 120,
          y: 50 + Math.floor(idx / 5) * 100
        };
      });
      setTablePositions(positions);
    } catch (error) {
      console.error('Failed to fetch tables:', error);
    }
  }, [api, selectedRestaurant?.id]);

  useEffect(() => {
    if (selectedRestaurant?.id) {
      fetchTables();
    }
  }, [selectedRestaurant?.id, fetchTables]);

  // Track printer connection status
  useEffect(() => {
    const handleConnected = () => setPrinterConnected(true);
    const handleDisconnected = () => setPrinterConnected(false);
    
    // Check initial state
    setPrinterConnected(printBridge.isConnected());
    
    // Listen for changes
    printBridge.on('connected', handleConnected);
    printBridge.on('disconnected', handleDisconnected);
    
    return () => {
      printBridge.off('connected', handleConnected);
      printBridge.off('disconnected', handleDisconnected);
    };
  }, []);

  // Print QR to thermal printer
  const handlePrintQRThermal = async (table) => {
    if (!printerConnected) {
      toast.error('No thermal printer connected', {
        description: 'Go to Print Settings to connect your Bluetooth printer first.',
        action: {
          label: 'Print Settings',
          onClick: () => window.location.href = '/restaurant/print-settings'
        }
      });
      return;
    }
    
    setPrinting(true);
    try {
      const qrUrl = `${window.location.origin}/table-order/${selectedRestaurant.id}?table=${table.id}`;
      await printBridge.printTableQR(table, selectedRestaurant, qrUrl);
      toast.success(`QR code printed for Table ${table.table_number}`);
    } catch (error) {
      toast.error(`Print failed: ${error.message}`);
    } finally {
      setPrinting(false);
    }
  };

  // Print all QRs to thermal printer
  const handlePrintAllQRsThermal = async () => {
    if (!printerConnected) {
      toast.error('No thermal printer connected', {
        description: 'Go to Print Settings to connect your Bluetooth printer first.',
        action: {
          label: 'Print Settings',
          onClick: () => window.location.href = '/restaurant/print-settings'
        }
      });
      return;
    }
    
    const activeTables = tables.filter(t => t.is_active);
    if (activeTables.length === 0) {
      toast.error('No active tables to print');
      return;
    }
    
    setPrinting(true);
    toast.info(`Printing ${activeTables.length} QR codes...`);
    
    try {
      await printBridge.printAllTableQRs(tables, selectedRestaurant, window.location.origin);
      toast.success(`All ${activeTables.length} QR codes printed!`);
    } catch (error) {
      toast.error(`Print failed: ${error.message}`);
    } finally {
      setPrinting(false);
    }
  };

  // Fallback browser print for QR codes (letter size)
  const handleBrowserPrintQR = (table) => {
    const printWindow = window.open('', '_blank');
    const qrUrl = `${window.location.origin}/table-order/${selectedRestaurant.id}?table=${table.id}`;
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Table ${table.table_number} QR Code</title>
          <style>
            body { 
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; 
              display: flex; 
              justify-content: center; 
              align-items: center; 
              min-height: 100vh;
              margin: 0;
            }
            .container { 
              text-align: center; 
              border: 3px solid #e5e7eb; 
              border-radius: 24px; 
              padding: 40px;
            }
            .table-num { font-size: 36px; font-weight: bold; color: #1e293b; margin-bottom: 8px; }
            .info { font-size: 16px; color: #64748b; margin-bottom: 24px; }
            .qr { margin: 0 auto; }
            .cta { font-size: 24px; font-weight: 600; color: #f97316; margin-top: 24px; }
            .restaurant { font-size: 14px; color: #94a3b8; margin-top: 8px; }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="table-num">Table ${table.table_number}</div>
            <div class="info">${table.zone} · ${table.capacity} seats</div>
            <img class="qr" src="https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(qrUrl)}" alt="QR Code" />
            <div class="cta">Scan to Order</div>
            <div class="restaurant">${selectedRestaurant.name}</div>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  // Table CRUD
  const handleSaveTable = async () => {
    if (!tableForm.table_number) {
      toast.error('Please enter a table number');
      return;
    }
    
    try {
      if (editingTable) {
        await api.put(`/restaurants/${selectedRestaurant.id}/tables/${editingTable.id}`, tableForm);
        toast.success('Table updated successfully');
      } else {
        await api.post(`/restaurants/${selectedRestaurant.id}/tables`, tableForm);
        toast.success('Table created successfully');
      }
      setShowTableDialog(false);
      resetTableForm();
      fetchTables();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save table');
    }
  };

  const handleDeleteTable = async (tableId) => {
    if (!window.confirm('Are you sure you want to delete this table?')) return;
    
    try {
      await api.delete(`/restaurants/${selectedRestaurant.id}/tables/${tableId}`);
      toast.success('Table deleted successfully');
      fetchTables();
    } catch (error) {
      toast.error('Failed to delete table');
    }
  };

  const handleEditTable = (table) => {
    setEditingTable(table);
    setTableForm({
      table_number: table.table_number,
      capacity: table.capacity,
      zone: table.zone,
      description: table.description || '',
      is_active: table.is_active
    });
    setShowTableDialog(true);
  };

  const resetTableForm = () => {
    setEditingTable(null);
    setTableForm({
      table_number: '',
      capacity: 2,
      zone: 'Main',
      description: '',
      is_active: true
    });
  };

  // Floor plan drag handlers
  const handleTableDragStart = (e, tableId) => {
    setDraggingTable(tableId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleTableDrag = (e) => {
    if (!draggingTable || !floorPlanRef.current) return;
    e.preventDefault();
  };

  const handleTableDrop = async (e) => {
    if (!draggingTable || !floorPlanRef.current) return;
    e.preventDefault();
    
    const rect = floorPlanRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / floorPlanZoom;
    const y = (e.clientY - rect.top) / floorPlanZoom;
    
    // Update local state immediately for smooth UX
    setTablePositions(prev => ({
      ...prev,
      [draggingTable]: { x: Math.max(0, x - 40), y: Math.max(0, y - 30) }
    }));
    
    // Save position to backend
    try {
      await api.put(`/restaurants/${selectedRestaurant.id}/tables/${draggingTable}`, {
        position: { x: Math.max(0, x - 40), y: Math.max(0, y - 30) }
      });
    } catch (error) {
      console.error('Failed to save table position:', error);
    }
    
    setDraggingTable(null);
  };

  const handleFloorPlanDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  // Group tables by zone
  const tablesByZone = tables.reduce((acc, table) => {
    const zone = table.zone || 'Main';
    if (!acc[zone]) acc[zone] = [];
    acc[zone].push(table);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-10 bg-slate-200 rounded w-48 animate-pulse"></div>
        <div className="grid gap-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-32 bg-slate-200 rounded-xl animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  // Layout handles "no restaurant" case - this page only renders when restaurant exists
  return (
    <div className="space-y-6" data-testid="table-management-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{t('tableMgmt.title')}</h1>
          <p className="text-slate-500 mt-1">{t('tableMgmt.subtitle') || 'Manage your restaurant tables and booking availability'}</p>
        </div>
        
        {/* Restaurant Selector - only shown if user has multiple restaurants */}
        {restaurants.length > 1 && (
          <Select 
            value={selectedRestaurant?.id} 
            onValueChange={(id) => setSelectedRestaurant(restaurants.find(r => r.id === id))}
          >
            <SelectTrigger className="w-64" data-testid="restaurant-selector">
              <Store className="w-4 h-4 mr-2" />
              <SelectValue placeholder={t('restSettings.selectRestaurant')} />
            </SelectTrigger>
            <SelectContent>
              {restaurants.map(r => (
                <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
      
      <Tabs defaultValue="tables" className="space-y-6">
        <TabsList className="grid w-full max-w-xl grid-cols-3">
          <TabsTrigger value="tables" data-testid="tables-tab">
            <MapPin className="w-4 h-4 mr-2" />
            {t('tableMgmt.tables') || 'Tables'} ({tables.length})
          </TabsTrigger>
          <TabsTrigger value="floorplan" data-testid="floorplan-tab">
            <LayoutGrid className="w-4 h-4 mr-2" />
            Floor Plan
          </TabsTrigger>
          <TabsTrigger value="qrcodes" data-testid="qrcodes-tab">
            <QrCode className="w-4 h-4 mr-2" />
            QR Codes
          </TabsTrigger>
        </TabsList>

        {/* Tables Tab */}
        <TabsContent value="tables" className="space-y-6">
          <div className="flex justify-end">
            <Button 
              onClick={() => { resetTableForm(); setShowTableDialog(true); }}
              className="btn-primary"
              data-testid="add-table-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Table
            </Button>
          </div>

          {tables.length === 0 ? (
            <Card>
              <CardContent className="py-16 text-center">
                <MapPin className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 mb-2">No tables configured</h3>
                <p className="text-slate-500 mb-4">Add your restaurant tables to enable table bookings</p>
                <Button onClick={() => { resetTableForm(); setShowTableDialog(true); }} className="btn-primary">
                  <Plus className="w-4 h-4 mr-2" /> Add Your First Table
                </Button>
              </CardContent>
            </Card>
          ) : (
            Object.entries(tablesByZone).map(([zone, zoneTables]) => (
              <Card key={zone}>
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-orange-500" />
                    {zone}
                    <span className="text-sm font-normal text-slate-500">({zoneTables.length} tables)</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {zoneTables.map(table => (
                      <div
                        key={table.id}
                        className={`p-4 rounded-xl border-2 transition-all ${
                          table.is_active 
                            ? 'bg-white border-slate-200 hover:border-orange-300' 
                            : 'bg-slate-50 border-slate-200 opacity-60'
                        }`}
                        data-testid={`table-${table.id}`}
                      >
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <h4 className="font-semibold text-lg text-slate-900">
                              Table {table.table_number}
                            </h4>
                            <div className="flex items-center gap-1 text-sm text-slate-500 mt-1">
                              <Users className="w-4 h-4" />
                              <span>{table.capacity} seats</span>
                            </div>
                          </div>
                          {table.is_active ? (
                            <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                              Active
                            </span>
                          ) : (
                            <span className="px-2 py-1 bg-slate-200 text-slate-500 text-xs font-medium rounded-full">
                              Inactive
                            </span>
                          )}
                        </div>
                        
                        {table.description && (
                          <p className="text-sm text-slate-500 mb-3">{table.description}</p>
                        )}
                        
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEditTable(table)}
                            data-testid={`edit-table-${table.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-500 hover:text-red-600 hover:bg-red-50"
                            onClick={() => handleDeleteTable(table.id)}
                            data-testid={`delete-table-${table.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>

        {/* Floor Plan Tab */}
        <TabsContent value="floorplan" className="space-y-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Floor Plan Layout</h2>
              <p className="text-sm text-slate-500">Drag tables to arrange your restaurant layout</p>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setFloorPlanZoom(prev => Math.max(0.5, prev - 0.1))}
                data-testid="zoom-out-btn"
              >
                <ZoomOut className="w-4 h-4" />
              </Button>
              <span className="text-sm text-slate-500 w-12 text-center">{Math.round(floorPlanZoom * 100)}%</span>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setFloorPlanZoom(prev => Math.min(1.5, prev + 0.1))}
                data-testid="zoom-in-btn"
              >
                <ZoomIn className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Zone Legend */}
          <div className="flex flex-wrap gap-3">
            {ZONES.map(zone => {
              const colors = ZONE_COLORS[zone];
              const count = tablesByZone[zone]?.length || 0;
              return (
                <div key={zone} className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${colors.bg} ${colors.text} text-sm`}>
                  <div className={`w-3 h-3 rounded-full ${colors.border} border-2`} style={{ backgroundColor: colors.fill }}></div>
                  {zone} ({count})
                </div>
              );
            })}
          </div>

          {tables.length === 0 ? (
            <Card>
              <CardContent className="py-16 text-center">
                <LayoutGrid className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 mb-2">No tables to display</h3>
                <p className="text-slate-500 mb-4">Add tables first to create your floor plan</p>
                <Button onClick={() => { resetTableForm(); setShowTableDialog(true); }} className="btn-primary">
                  <Plus className="w-4 h-4 mr-2" /> Add Your First Table
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card className="overflow-hidden">
              <CardContent className="p-0">
                <div 
                  ref={floorPlanRef}
                  className="relative bg-slate-50 border-2 border-dashed border-slate-200 overflow-auto"
                  style={{ 
                    height: '500px',
                    backgroundImage: 'radial-gradient(circle, #e2e8f0 1px, transparent 1px)',
                    backgroundSize: `${20 * floorPlanZoom}px ${20 * floorPlanZoom}px`
                  }}
                  onDragOver={handleFloorPlanDragOver}
                  onDrop={handleTableDrop}
                  data-testid="floor-plan-canvas"
                >
                  <div 
                    className="relative"
                    style={{ 
                      transform: `scale(${floorPlanZoom})`,
                      transformOrigin: 'top left',
                      width: `${100 / floorPlanZoom}%`,
                      height: `${100 / floorPlanZoom}%`,
                      minWidth: '800px',
                      minHeight: '500px'
                    }}
                  >
                    {tables.map(table => {
                      const position = tablePositions[table.id] || { x: 50, y: 50 };
                      const colors = ZONE_COLORS[table.zone] || ZONE_COLORS['Main'];
                      const size = Math.min(80, 50 + table.capacity * 5);
                      
                      return (
                        <div
                          key={table.id}
                          draggable
                          onDragStart={(e) => handleTableDragStart(e, table.id)}
                          onDrag={handleTableDrag}
                          className={`absolute cursor-move transition-shadow hover:shadow-lg rounded-lg border-2 ${colors.border} ${colors.bg} flex flex-col items-center justify-center select-none ${
                            draggingTable === table.id ? 'opacity-50 shadow-xl' : ''
                          } ${!table.is_active ? 'opacity-40' : ''}`}
                          style={{
                            left: position.x,
                            top: position.y,
                            width: size,
                            height: size,
                          }}
                          data-testid={`floorplan-table-${table.id}`}
                        >
                          <Move className="w-3 h-3 text-slate-400 absolute top-1 right-1" />
                          <span className={`font-bold text-lg ${colors.text}`}>{table.table_number}</span>
                          <div className="flex items-center gap-0.5 text-xs text-slate-500">
                            <Users className="w-3 h-3" />
                            <span>{table.capacity}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                
                {/* Floor plan instructions */}
                <div className="px-4 py-3 bg-slate-100 border-t text-sm text-slate-500 flex items-center gap-2">
                  <Move className="w-4 h-4" />
                  Drag tables to reposition them. Changes are saved automatically.
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Availability Tab */}
        {/* QR Codes Tab */}
        <TabsContent value="qrcodes" className="space-y-6">
          {/* Printer Status Banner */}
          {printerConnected ? (
            <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-full">
                  <Bluetooth className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="font-medium text-green-800">Thermal Printer Connected</p>
                  <p className="text-sm text-green-600">QR codes will print directly to your thermal printer</p>
                </div>
              </div>
              <Button
                onClick={handlePrintAllQRsThermal}
                className="bg-green-600 hover:bg-green-700"
                disabled={printing || tables.filter(t => t.is_active).length === 0}
                data-testid="thermal-print-all-qr-btn"
              >
                {printing ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Printing...
                  </span>
                ) : (
                  <>
                    <Printer className="w-4 h-4 mr-2" />
                    Print All to Thermal ({tables.filter(t => t.is_active).length})
                  </>
                )}
              </Button>
            </div>
          ) : (
            <div className="flex items-center justify-between p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-full">
                  <AlertCircle className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="font-medium text-amber-800">No Thermal Printer Connected</p>
                  <p className="text-sm text-amber-600">Connect your Bluetooth printer for receipt-sized QR codes</p>
                </div>
              </div>
              <Button
                variant="outline"
                className="border-amber-300 text-amber-700 hover:bg-amber-100"
                onClick={() => window.location.href = '/restaurant/print-settings'}
                data-testid="go-to-print-settings-btn"
              >
                <Bluetooth className="w-4 h-4 mr-2" />
                Connect Printer
              </Button>
            </div>
          )}

          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Table QR Codes</h2>
              <p className="text-sm text-slate-500">Generate and print QR codes for customers to scan and order from their table</p>
            </div>
            {/* Browser print fallback button */}
            <Button
              variant="outline"
              onClick={() => {
                const printWindow = window.open('', '_blank');
                const qrContent = tables.filter(t => t.is_active).map(table => {
                  const qrUrl = `${window.location.origin}/table-order/${selectedRestaurant.id}?table=${table.id}`;
                  return `
                    <div style="display: inline-block; margin: 20px; text-align: center; page-break-inside: avoid; border: 2px solid #e5e7eb; border-radius: 16px; padding: 24px;">
                      <div style="font-size: 28px; font-weight: bold; margin-bottom: 12px; color: #1e293b;">Table ${table.table_number}</div>
                      <div style="font-size: 14px; color: #64748b; margin-bottom: 16px;">${table.zone} · ${table.capacity} seats</div>
                      <img src="https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(qrUrl)}" alt="QR Code" style="width: 200px; height: 200px;" />
                      <div style="margin-top: 16px; font-size: 18px; font-weight: 600; color: #f97316;">Scan to Order</div>
                      <div style="font-size: 12px; color: #94a3b8; margin-top: 4px;">${selectedRestaurant.name}</div>
                    </div>
                  `;
                }).join('');
                printWindow.document.write(`
                  <!DOCTYPE html>
                  <html>
                    <head>
                      <title>Table QR Codes - ${selectedRestaurant.name}</title>
                      <style>
                        body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 20px; }
                        @media print { body { padding: 0; } }
                      </style>
                    </head>
                    <body>${qrContent}</body>
                  </html>
                `);
                printWindow.document.close();
                printWindow.print();
              }}
              disabled={tables.filter(t => t.is_active).length === 0}
              data-testid="print-all-qr-btn"
            >
              <Printer className="w-4 h-4 mr-2" />
              Print to Paper (A4)
            </Button>
          </div>

          {tables.filter(t => t.is_active).length === 0 ? (
            <Card>
              <CardContent className="py-16 text-center">
                <QrCode className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 mb-2">No active tables</h3>
                <p className="text-slate-500 mb-4">Add tables first to generate QR codes</p>
                <Button onClick={() => { resetTableForm(); setShowTableDialog(true); }} className="btn-primary">
                  <Plus className="w-4 h-4 mr-2" /> Add Table
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {tables.filter(t => t.is_active).map(table => {
                const qrUrl = `${window.location.origin}/table-order/${selectedRestaurant.id}?table=${table.id}`;
                return (
                  <Card key={table.id} className="overflow-hidden" data-testid={`qr-card-${table.id}`}>
                    <CardHeader className="pb-2 bg-gradient-to-r from-orange-500 to-orange-600 text-white">
                      <CardTitle className="text-lg">Table {table.table_number}</CardTitle>
                      <CardDescription className="text-orange-100">
                        {table.zone} · {table.capacity} seats
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="pt-6 flex flex-col items-center">
                      <div className="bg-white p-4 rounded-xl shadow-sm border">
                        <QRCodeSVG 
                          value={qrUrl}
                          size={160}
                          level="H"
                          includeMargin={true}
                          data-testid={`qr-code-${table.id}`}
                        />
                      </div>
                      <p className="text-xs text-slate-400 mt-3 text-center break-all max-w-[200px]">
                        {qrUrl}
                      </p>
                      <div className="flex flex-col gap-2 mt-4 w-full">
                        {/* Thermal Print Button (Primary when connected) */}
                        {printerConnected && (
                          <Button
                            size="sm"
                            className="w-full bg-green-600 hover:bg-green-700"
                            onClick={() => handlePrintQRThermal(table)}
                            disabled={printing}
                            data-testid={`thermal-print-qr-${table.id}`}
                          >
                            {printing ? (
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                            ) : (
                              <Bluetooth className="w-4 h-4 mr-1" />
                            )}
                            Print to Thermal
                          </Button>
                        )}
                        
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex-1"
                            onClick={() => {
                              const svg = document.querySelector(`[data-testid="qr-code-${table.id}"]`);
                              const svgData = new XMLSerializer().serializeToString(svg);
                              const canvas = document.createElement('canvas');
                              const ctx = canvas.getContext('2d');
                              const img = new Image();
                              img.onload = () => {
                                canvas.width = img.width;
                                canvas.height = img.height;
                                ctx.fillStyle = 'white';
                                ctx.fillRect(0, 0, canvas.width, canvas.height);
                                ctx.drawImage(img, 0, 0);
                                const pngFile = canvas.toDataURL('image/png');
                                const downloadLink = document.createElement('a');
                                downloadLink.download = `table-${table.table_number}-qr.png`;
                                downloadLink.href = pngFile;
                                downloadLink.click();
                              };
                              img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
                            }}
                            data-testid={`download-qr-${table.id}`}
                          >
                            <Download className="w-4 h-4 mr-1" />
                            Download
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex-1"
                            onClick={() => handleBrowserPrintQR(table)}
                            data-testid={`print-qr-${table.id}`}
                          >
                            <Printer className="w-4 h-4 mr-1" />
                            Paper
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Add/Edit Table Dialog */}
      <Dialog open={showTableDialog} onOpenChange={setShowTableDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingTable ? 'Edit Table' : 'Add New Table'}</DialogTitle>
            <DialogDescription>
              {editingTable ? 'Update the table details' : 'Create a new table for reservations'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="table_number">Table Number *</Label>
                <Input
                  id="table_number"
                  placeholder="e.g., 1, A1, VIP1"
                  value={tableForm.table_number}
                  onChange={(e) => setTableForm(prev => ({ ...prev, table_number: e.target.value }))}
                  data-testid="table-number-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="capacity">Capacity *</Label>
                <Input
                  id="capacity"
                  type="number"
                  min="1"
                  max="50"
                  value={tableForm.capacity}
                  onChange={(e) => setTableForm(prev => ({ ...prev, capacity: parseInt(e.target.value) || 1 }))}
                  data-testid="table-capacity-input"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="zone">Zone</Label>
              <Select 
                value={tableForm.zone} 
                onValueChange={(value) => setTableForm(prev => ({ ...prev, zone: value }))}
              >
                <SelectTrigger data-testid="table-zone-select">
                  <SelectValue placeholder="Select zone" />
                </SelectTrigger>
                <SelectContent>
                  {ZONES.map(zone => (
                    <SelectItem key={zone} value={zone}>{zone}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="description">Description (optional)</Label>
              <Input
                id="description"
                placeholder="e.g., Window seat, near fireplace"
                value={tableForm.description}
                onChange={(e) => setTableForm(prev => ({ ...prev, description: e.target.value }))}
                data-testid="table-description-input"
              />
            </div>
            
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <Label htmlFor="is_active">Active</Label>
                <p className="text-sm text-slate-500">Table is available for bookings</p>
              </div>
              <Switch
                id="is_active"
                checked={tableForm.is_active}
                onCheckedChange={(checked) => setTableForm(prev => ({ ...prev, is_active: checked }))}
                data-testid="table-active-switch"
              />
            </div>
          </div>
          
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setShowTableDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveTable} className="btn-primary" data-testid="save-table-btn">
              <Save className="w-4 h-4 mr-2" />
              {editingTable ? 'Update Table' : 'Add Table'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
